// Runtime renderer for a "scrollable_layout" custom viewer config built in the
// Viewer Creation Lab. See ViewerCreationLab.jsx for the schema. Phase (b) of
// the case-page-builder feature.
//
// Inputs come from URL params on /DV_Viewer (custom_viewer_id, study_id,
// patient_id). This component fetches the viewer config + studyData + reader
// queue, then renders the columns/components with real interactivity:
//   - Submit click reveals the components listed in its props.reveals
//   - Slider / input / textarea track local state
//   - Viewer components launch an embedded OHIF iframe for the current patient
//   - Nav component walks the reader queue (my-queue + case-completed)
//   - Each viewer has an expand button that fullscreens it

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "react-toastify";
import { AIModelSelector } from "../ai_models";
import { runModel, getExecutionStatus } from "../../services/aiModels";
import API, { ensureOrthancAuthCookie } from "../../services/api.ts";
import "./ScrollableLayoutRenderer.css";
import { resolveAllowedViewerUrl } from '../../utils/safeUrl';

// Reserved answer-key used to persist a random_one / weighted_one reveal pick
// alongside the rest of the form state, so the chosen group is restored on
// reload (otherwise the reviewer's "next case" / AI panel disappears after a
// browser refresh).
const CHOSEN_REVEAL_PREFIX = "__chosen_reveal__";
const chosenRevealKey = (submitId) => `${CHOSEN_REVEAL_PREFIX}${submitId}`;

// `patientId` is now a controlled prop owned by the parent (DvViewerScrollable)
// so the top navbar's multi-patient nav can drive which patient is rendered.
// `patientIds` (optional) is the parent-selected multi-patient set; when it
// has 2+ entries the in-layout Prev/Next walks that list instead of the
// study's reader queue, so case selection matches what the top navbar shows.
// `onPatientChange(newPatientId)` is called by the in-layout Nav (queue mode).
const ScrollableLayoutRenderer = ({ customViewerId, studyId, patientId, patientIds, onPatientChange }) => {
    const [config, setConfig] = useState(null);
    const [studyData, setStudyData] = useState([]);
    const [queue, setQueue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [formState, setFormState] = useState({}); // { componentId: value }
    const [clickedSubmits, setClickedSubmits] = useState(new Set()); // ids of submits that have been clicked
    // For submits with reveal_mode 'random_one' / 'weighted_one', the runtime
    // picks one of the listed reveals at click time and stores the choice here
    // so re-renders stay consistent until the patient changes.
    const [chosenReveals, setChosenReveals] = useState({}); // { submitId: componentId }
    const [expandedViewerId, setExpandedViewerId] = useState(null);
    // Field ids that failed the most recent submit's `required_fields` check.
    // Drives the red-border/error state on inputs; cleared as the reviewer
    // starts filling each one, or when the patient changes.
    const [invalidFieldIds, setInvalidFieldIds] = useState(new Set());

    // When the active patient changes (via top navbar or queue Nav), wipe the
    // case-scoped state so we don't leak the previous case's answers/reveals
    // into the new one. Then re-hydrate from any saved ViewerFormSubmission
    // rows for this (viewer, study, patient, user) so the reviewer sees
    // their previous answers when revisiting a case.
    useEffect(() => {
        setFormState({});
        setClickedSubmits(new Set());
        setChosenReveals({});
        setExpandedViewerId(null);
        setInvalidFieldIds(new Set());

        if (!customViewerId || !patientId) return;
        let cancelled = false;
        const params = new URLSearchParams({ patient_id: patientId });
        if (studyId) params.append("study_id", String(studyId));
        API.get(`/viewers/${customViewerId}/submissions/?${params.toString()}`)
            .then((res) => {
                if (cancelled) return;
                const subs = res?.data?.submissions || {};
                // Merge across submit_ids — later writes win. The runtime
                // never stores _different_ values under different submit_ids
                // for the same field (we always save the full form_state),
                // but in case the schema changes over time, prefer the most
                // recently submitted batch.
                const ordered = Object.values(subs).sort((a, b) =>
                    (a.submitted_at || "").localeCompare(b.submitted_at || "")
                );
                const merged = {};
                const clickedSubmitIds = new Set();
                for (const s of ordered) {
                    Object.assign(merged, s.answers || {});
                    if (s.submit_id) clickedSubmitIds.add(s.submit_id);
                }
                // Restore random_one / weighted_one picks so any
                // previously-selected reveal group reappears after refresh.
                const restoredChosen = {};
                for (const k of Object.keys(merged)) {
                    if (k.startsWith(CHOSEN_REVEAL_PREFIX)) {
                        const sid = k.slice(CHOSEN_REVEAL_PREFIX.length);
                        if (sid) restoredChosen[sid] = merged[k];
                    }
                }
                if (Object.keys(merged).length > 0) setFormState(merged);
                if (clickedSubmitIds.size > 0) setClickedSubmits(clickedSubmitIds);
                if (Object.keys(restoredChosen).length > 0) setChosenReveals(restoredChosen);
            })
            .catch((err) => {
                // 401/403 just means we shouldn't pre-fill; 404 means viewer
                // moved. Either way, fall through with empty form state.
                console.warn("[ScrollableRenderer] load submissions failed:", err);
            });
        return () => { cancelled = true; };
    }, [patientId, customViewerId, studyId]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [viewerResp, dataResp, queueResp] = await Promise.all([
                    API.get(`/viewers/${customViewerId}/`),
                    API.get(`/studies/${studyId}/get_study_data/`),
                    API.get(`/studies/${studyId}/my-queue/`).catch(() => ({ data: null })),
                ]);
                if (cancelled) return;
                setConfig(viewerResp.data?.config || {});
                setStudyData(dataResp.data || []);
                setQueue(queueResp.data || null);
            } catch (err) {
                if (!cancelled) setError(err?.response?.data?.detail || "Failed to load case layout.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [customViewerId, studyId]);

    // Components hidden by default = those referenced in any submit's reveals
    // list but whose submit hasn't been clicked yet. Walks into groups so a
    // submit can target a top-level component OR a nested-in-group one.
    const flattenAll = (components) => {
        const out = [];
        for (const c of components || []) {
            out.push(c);
            if (c.type === "group" && Array.isArray(c.components)) {
                out.push(...flattenAll(c.components));
            }
        }
        return out;
    };
    const allComponents = useMemo(
        () => flattenAll((config?.scrollable_layout?.columns || []).flatMap((c) => c.components || [])),
        [config]
    );

    const hiddenByDefault = useMemo(() => {
        const ids = new Set();
        allComponents.forEach((c) => {
            if (c.type === "submit" && Array.isArray(c.props?.reveals)) {
                c.props.reveals.forEach((rid) => ids.add(rid));
            }
        });
        return ids;
    }, [allComponents]);

    const revealedIds = useMemo(() => {
        const out = new Set();
        clickedSubmits.forEach((submitId) => {
            const submit = allComponents.find((c) => c.id === submitId);
            if (!submit) return;
            const reveals = Array.isArray(submit.props?.reveals) ? submit.props.reveals : [];
            const mode = submit.props?.reveal_mode || "all";
            if (mode === "all") {
                reveals.forEach((rid) => out.add(rid));
            } else {
                const chosen = chosenReveals[submitId];
                if (chosen) out.add(chosen);
            }
        });
        return out;
    }, [clickedSubmits, chosenReveals, allComponents]);

    const isVisible = (componentId) => !hiddenByDefault.has(componentId) || revealedIds.has(componentId);

    const updateField = (componentId, value) => {
        setFormState((s) => ({ ...s, [componentId]: value }));
        // Clear the red "required" highlight as soon as the reviewer starts
        // filling the field they were prompted about.
        setInvalidFieldIds((prev) => {
            if (!prev.has(componentId)) return prev;
            const next = new Set(prev);
            next.delete(componentId);
            return next;
        });
    };

    // Weighted random pick. Falls back to uniform if weights are missing/all zero.
    const pickWeighted = (ids, weights) => {
        if (!ids.length) return null;
        const wmap = weights && typeof weights === "object" ? weights : {};
        const wlist = ids.map((id) => {
            const n = Number(wmap[id]);
            return Number.isFinite(n) && n >= 0 ? n : 1;
        });
        const total = wlist.reduce((a, b) => a + b, 0);
        if (total <= 0) return ids[Math.floor(Math.random() * ids.length)];
        let r = Math.random() * total;
        for (let i = 0; i < ids.length; i++) {
            r -= wlist[i];
            if (r <= 0) return ids[i];
        }
        return ids[ids.length - 1];
    };

    // Wall-clock time the reviewer first mounted the current case. Sent with
    // each submission so the backend can record per-row time spent that
    // includes the work done before the first submit (otherwise the very
    // first row would always show 0s). Reset whenever the patient changes.
    const caseOpenedAtRef = useRef(new Date().toISOString());
    useEffect(() => {
        caseOpenedAtRef.current = new Date().toISOString();
    }, [patientId, customViewerId]);

    // Fields locked by any already-clicked submit with `lock_after_submit: true`.
    // Derived from `clickedSubmits` so that lock state survives a patient
    // round-trip: after refresh / re-navigation the patient-load effect
    // restores clickedSubmits from saved submissions, and this re-derives
    // the lock set without needing a separate persistence path.
    const lockedFieldIds = useMemo(() => {
        const out = new Set();
        const FIELD_TYPES = new Set(["input", "textarea", "slider"]);
        clickedSubmits.forEach((submitId) => {
            const submit = allComponents.find((c) => c.id === submitId);
            if (!submit?.props?.lock_after_submit) return;
            const fields = Array.isArray(submit.props?.submit_fields) ? submit.props.submit_fields : [];
            const toLock = fields.length > 0
                ? fields
                : allComponents.filter((c) => FIELD_TYPES.has(c.type)).map((c) => c.id);
            for (const id of toLock) out.add(id);
        });
        return out;
    }, [clickedSubmits, allComponents]);

    // Union of all fields marked as required by any submit-button in the
    // layout. Drives the red asterisk next to labels so reviewers know a
    // field is expected before they click any submit. Per-click validation
    // still happens against the specific button's list (below).
    const requiredFieldIds = useMemo(() => {
        const out = new Set();
        for (const c of allComponents) {
            if (c.type !== "submit") continue;
            const req = Array.isArray(c.props?.required_fields) ? c.props.required_fields : [];
            for (const id of req) out.add(id);
        }
        return out;
    }, [allComponents]);

    // Persist the reviewer's current answers under a (viewer, study, patient,
    // user, submit_id) key. Empty-string submit_id is reserved for nav-driven
    // saves ("auto-save before Next/Prev/Finish") so a reviewer who fills the
    // form but never clicks a Submit-type button doesn't silently lose their
    // work. We skip empty form states to avoid writing junk rows.
    //
    // `fieldFilter` (optional) is a list of component ids to scope the saved
    // payload to. Empty/undefined → save every field in `formState`. This is
    // what powers the lab's "Fields submitted on click" picker so a submit
    // button can commit only the answers it owns.
    //
    // Returns a Promise so navigation handlers can await it; callers that
    // don't care can fire-and-forget.
    const persistFormState = useCallback((submitId, fieldFilter, extraAnswers) => {
        if (!customViewerId || !patientId) return Promise.resolve();
        const all = formState || {};
        let answers = all;
        if (Array.isArray(fieldFilter) && fieldFilter.length > 0) {
            answers = {};
            for (const k of fieldFilter) {
                if (k in all) answers[k] = all[k];
            }
            // Reserved internal keys (chosen-reveal markers) must survive
            // the field filter so random_one picks aren't dropped.
            for (const k of Object.keys(all)) {
                if (k.startsWith(CHOSEN_REVEAL_PREFIX)) answers[k] = all[k];
            }
        }
        if (extraAnswers && typeof extraAnswers === "object") {
            answers = { ...answers, ...extraAnswers };
        }
        if (!answers || Object.keys(answers).length === 0) return Promise.resolve();
        return API.post(`/viewers/${customViewerId}/submissions/`, {
            study_id: studyId || null,
            patient_id: patientId,
            submit_id: submitId || "",
            answers,
            case_opened_at: caseOpenedAtRef.current,
        }).catch((err) => {
            console.warn("[ScrollableRenderer] save submission failed:", err);
        });
    }, [customViewerId, patientId, studyId, formState]);

    const handleSubmitClick = (componentId) => {
        const submit = allComponents.find((c) => c.id === componentId);

        // Required-fields gate: block the click if any tick-listed field is
        // empty. Sliders always carry a numeric value so they never trip the
        // gate; only input/textarea (string) values need to be non-blank.
        // Missing fields get red-bordered until the reviewer starts typing.
        const requiredFields = Array.isArray(submit?.props?.required_fields)
            ? submit.props.required_fields
            : [];
        if (requiredFields.length > 0) {
            const missing = [];
            for (const fid of requiredFields) {
                const v = formState[fid];
                const empty =
                    v === null ||
                    v === undefined ||
                    (typeof v === "string" && v.trim() === "");
                if (empty) {
                    const comp = allComponents.find((c) => c.id === fid);
                    const label = comp?.props?.label || comp?.props?.title || fid;
                    missing.push({ id: fid, label });
                }
            }
            if (missing.length > 0) {
                setInvalidFieldIds(new Set(missing.map((m) => m.id)));
                toast.error(
                    `Please fill required field${missing.length === 1 ? "" : "s"}: ${missing
                        .map((m) => m.label)
                        .join(", ")}`
                );
                return;
            }
        }

        const reveals = Array.isArray(submit?.props?.reveals) ? submit.props.reveals : [];
        const mode = submit?.props?.reveal_mode || "all";
        let chosenForThisClick = null;
        if ((mode === "random_one" || mode === "weighted_one") && reveals.length > 0) {
            const choice = mode === "weighted_one"
                ? pickWeighted(reveals, submit.props?.reveal_weights)
                : reveals[Math.floor(Math.random() * reveals.length)];
            chosenForThisClick = choice;
            setChosenReveals((prev) => ({ ...prev, [componentId]: choice }));
            // Mirror into formState so navigation-driven auto-saves keep
            // the chosen reveal in answers, not just this Submit's payload.
            setFormState((s) => ({ ...s, [chosenRevealKey(componentId)]: choice }));
        }
        setClickedSubmits((prev) => new Set(prev).add(componentId));
        // Scope the saved payload to the submit button's configured field
        // list (if any). Empty/missing → save the whole form, matching the
        // legacy single-submit behavior.
        const submitFields = Array.isArray(submit?.props?.submit_fields) ? submit.props.submit_fields : [];
        const extra = chosenForThisClick
            ? { [chosenRevealKey(componentId)]: chosenForThisClick }
            : undefined;
        persistFormState(componentId, submitFields, extra);
        // Locked field state is derived from clickedSubmits via useMemo above,
        // so adding to clickedSubmits is enough to lock the configured fields.
    };

    // ---- Case navigation ----
    // Two sources for the in-layout Prev/Next walk:
    //   (a) the parent-passed `patientIds` selection — used when the viewer
    //       was launched in multi-patient mode (multiple ids in the URL).
    //       In that mode the in-layout Nav should match the top navbar.
    //   (b) the study's reader queue — used when only a single patient was
    //       opened. Next still marks the case complete server-side.
    const usingSelection = Array.isArray(patientIds) && patientIds.length > 1;
    const queueList = queue?.queue || [];
    const navList = usingSelection
        ? patientIds.map((pid) => ({ patient_id: pid }))
        : queueList;
    const currentIndex = navList.findIndex((q) => q.patient_id === patientId);

    // Submit components that are currently reachable (visible per reveal
    // logic). When the reviewer has clicked every reachable submit, the
    // case is fully annotated — we auto-mark it complete server-side so
    // the Flow tab / queue progress updates without requiring an explicit
    // Next/Finish click. New submits revealed by a click expand this set,
    // so the auto-mark only fires once the *full* chain has been clicked.
    const reachableSubmitIds = useMemo(() => {
        const ids = new Set();
        for (const c of allComponents) {
            if (c.type !== "submit") continue;
            if (!hiddenByDefault.has(c.id) || revealedIds.has(c.id)) {
                ids.add(c.id);
            }
        }
        return ids;
    }, [allComponents, hiddenByDefault, revealedIds]);

    // Remember which patient we already auto-marked so re-renders, queue
    // refreshes, or backfilled `clickedSubmits` from saved submissions
    // don't fire the POST again.
    const autoCompletedPatientRef = useRef(null);
    useEffect(() => {
        autoCompletedPatientRef.current = null;
    }, [patientId]);

    useEffect(() => {
        if (!studyId || !patientId) return;
        // Selection mode walks an ad-hoc list rather than the reader's
        // assigned queue, so there's nothing to mark complete server-side.
        if (usingSelection) return;
        if (reachableSubmitIds.size === 0) return;
        if (autoCompletedPatientRef.current === patientId) return;
        for (const id of reachableSubmitIds) {
            if (!clickedSubmits.has(id)) return;
        }
        autoCompletedPatientRef.current = patientId;
        const markedPatient = patientId;
        (async () => {
            try {
                // mark-annotated drives the Study Dashboard's "annotated"
                // badge (it sets PatientAnnotation.annotation_completed_at,
                // which is what /annotated_patients/ checks). It also keeps
                // StudyCaseAssignment.completed_at in sync for randomized
                // studies, so a single call covers both the dashboard view
                // and the reader's queue progress.
                await API.post(
                    `/studies/${studyId}/mark-annotated/${encodeURIComponent(markedPatient)}/`,
                    { mark_complete: true }
                );
                // Refresh the queue so the navbar progress badge + the
                // in-layout Nav's "Next" target reflect the new state.
                try {
                    const res = await API.get(`/studies/${studyId}/my-queue/`);
                    if (res?.data) setQueue(res.data);
                } catch (e) {
                    console.warn("[ScrollableRenderer] queue refresh after auto-complete failed:", e);
                }
            } catch (e) {
                console.warn("[ScrollableRenderer] auto mark-annotated failed:", e);
                // Allow a retry if the user clicks another submit (or the
                // same chain completes again after a state change).
                if (autoCompletedPatientRef.current === markedPatient) {
                    autoCompletedPatientRef.current = null;
                }
            }
        })();
    }, [clickedSubmits, reachableSubmitIds, studyId, patientId, usingSelection]);

    // Hand the new patient up to the parent (DvViewerScrollable). State reset
    // happens automatically via the patientId-change effect above; URL sync is
    // handled by the parent so it can update the multi-patient index too.
    const goToPatient = useCallback((newPatientId) => {
        if (!newPatientId) return;
        if (onPatientChange) onPatientChange(newPatientId);
    }, [onPatientChange]);

    const handlePrev = async () => {
        if (currentIndex <= 0) return;
        // Save the current case's answers before walking back so the reviewer
        // doesn't lose typed-but-not-submitted text just from navigating.
        await persistFormState("");
        goToPatient(navList[currentIndex - 1].patient_id);
    };

    // Set to true after the reader finishes the last case in the queue —
    // drives the "Study finished" overlay below.
    const [studyFinished, setStudyFinished] = useState(false);

    const handleNext = async () => {
        // Auto-save the reviewer's current answers under the empty submit_id
        // before navigating — covers reviewers who fill text fields but never
        // click a dedicated Submit button. This runs in both selection-mode
        // and queue-mode.
        await persistFormState("");

        // In selection-mode we don't mark cases complete or rebuild the queue
        // — the navigation is purely the user's chosen list, not the study's
        // assigned reader queue. Show the "finished" overlay when the reader
        // clicks past the last selected patient.
        if (usingSelection) {
            if (currentIndex >= 0 && currentIndex + 1 < navList.length) {
                goToPatient(navList[currentIndex + 1].patient_id);
            } else {
                setStudyFinished(true);
            }
            return;
        }
        try {
            await API.post(`/studies/${studyId}/case-completed/${encodeURIComponent(patientId)}/`);
        } catch (e) {
            // Continue even if marking complete fails — the user can retry.
            console.warn("[ScrollableRenderer] case-completed failed:", e);
        }
        if (currentIndex >= 0 && currentIndex + 1 < navList.length) {
            goToPatient(navList[currentIndex + 1].patient_id);
        } else {
            // Refresh queue to get next_patient_id (may differ if queue rebuilt).
            // If the refresh returns no next patient, the reader has cleared
            // their assignments — surface that explicitly instead of leaving
            // the Finish click looking like a no-op.
            try {
                const res = await API.get(`/studies/${studyId}/my-queue/`);
                setQueue(res.data);
                if (res.data?.next_patient_id) {
                    goToPatient(res.data.next_patient_id);
                } else {
                    setStudyFinished(true);
                }
            } catch (e) {
                console.warn("[ScrollableRenderer] queue refresh failed:", e);
                // Last resort: still show the finished overlay so the user
                // isn't stuck looking at a non-responsive button.
                setStudyFinished(true);
            }
        }
    };

    if (loading) {
        return (
            <div className="slr-loading">
                <i className="fa fa-spinner fa-spin"></i>
                <p>Loading case layout…</p>
            </div>
        );
    }

    if (error || !config?.scrollable_layout?.columns) {
        return (
            <div className="slr-error">
                <i className="fa fa-exclamation-triangle"></i>
                <p>{error || "This viewer is not configured as a scrollable layout."}</p>
            </div>
        );
    }

    // Drop empty columns so a single-column layout fills the full width
    // (avoids the left-half rendering when an empty second column reserves 50%).
    const columns = (config.scrollable_layout.columns || []).filter(
        (col) => (col.components || []).length > 0
    );

    // Recursive renderer — handles groups by recursing into their children with
    // the same visibility rules. A hidden group hides all its children too.
    const renderComponent = (comp) => {
        if (!isVisible(comp.id)) return null;
        if (comp.type === "group") {
            return (
                <div key={comp.id} className="slr-card slr-group">
                    {comp.props?.title && <div className="slr-card-title">{comp.props.title}</div>}
                    {comp.props?.description && (
                        <div className="slr-card-body slr-group-description">{comp.props.description}</div>
                    )}
                    <div className="slr-group-children">
                        {(comp.components || []).map(renderComponent)}
                    </div>
                </div>
            );
        }
        return (
            <ComponentRenderer
                key={comp.id}
                comp={comp}
                value={formState[comp.id]}
                onChange={(v) => updateField(comp.id, v)}
                onSubmit={() => handleSubmitClick(comp.id)}
                submitted={clickedSubmits.has(comp.id)}
                locked={lockedFieldIds.has(comp.id)}
                required={requiredFieldIds.has(comp.id)}
                invalid={invalidFieldIds.has(comp.id)}
                studyId={studyId}
                patientId={patientId}
                studyData={studyData}
                expanded={expandedViewerId === comp.id}
                onToggleExpand={() =>
                    setExpandedViewerId((id) => (id === comp.id ? null : comp.id))
                }
                queueIndex={currentIndex}
                queueLength={navList.length}
                onPrev={handlePrev}
                onNext={handleNext}
            />
        );
    };

    return (
        <div className={`slr-container cols-${columns.length || 1}`}>
            {columns.map((col) => (
                <div key={col.id} className="slr-column">
                    {(col.components || []).map(renderComponent)}
                </div>
            ))}
            {studyFinished && (
                <StudyFinishedOverlay
                    onGoHome={() => { window.location.href = "/"; }}
                />
            )}
        </div>
    );
};

// Modal that pops once the reader clears their last queue case (or the
// last selected patient in multi-patient mode). Backdrop is non-dismissable
// on purpose — the only way out is the "Back to home" button — so the
// reader's flow ends with a clear "done" rather than getting stranded
// looking at the same case they just finished.
const StudyFinishedOverlay = ({ onGoHome }) => (
    <div className="slr-finished-overlay" role="dialog" aria-modal="true">
        <div className="slr-finished-modal">
            <div className="slr-finished-icon">
                <i className="fa fa-check-circle"></i>
            </div>
            <h3 className="slr-finished-title">Study finished</h3>
            <p className="slr-finished-body">
                You've completed every case assigned to you in this study. Your
                answers and any model runs have been saved.
            </p>
            <button type="button" className="btn btn-primary slr-finished-btn" onClick={onGoHome}>
                <i className="fa fa-home me-2"></i>Back to home
            </button>
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Per-component renderers
// ---------------------------------------------------------------------------

const ComponentRenderer = (props) => {
    const { comp } = props;
    switch (comp.type) {
        case "text":
            return <TextCard comp={comp} />;
        case "ai_panel":
            return <AIPanelCard {...props} />;
        case "viewer":
            if (comp.props?.viewer_type === "document-viewer") {
                return <EmbeddedDocument {...props} />;
            }
            return <EmbeddedViewer {...props} />;
        case "slider":
            return <SliderField {...props} />;
        case "input":
            return <InputField {...props} />;
        case "textarea":
            return <TextareaField {...props} />;
        case "submit":
            return <SubmitButton {...props} />;
        case "nav":
            return <NavButtons {...props} />;
        default:
            return null;
    }
};

const TextCard = ({ comp }) => (
    <div className="slr-card">
        {comp.props?.title && <div className="slr-card-title">{comp.props.title}</div>}
        {comp.props?.body && <div className="slr-card-body">{comp.props.body}</div>}
    </div>
);

// AI panel runtime card. Two modes:
//   1. Bound to a specific model (props.ai_model_id) — render exactly the
//      researcher-configured parameter form (with fixed/editable/open modes)
//      and a Run button that calls /ai-models/models/{id}/run/ directly.
//   2. Unbound — fall back to the navbar's AIModelSelector dropdown so the
//      reviewer can pick any model assigned to the study.
const AIPanelCard = ({ comp, studyId, patientId, studyData }) => {
    const props = comp.props || {};
    const modelId = props.ai_model_id;

    if (!modelId) {
        return (
            <div className="slr-card slr-ai-panel">
                {props.title && <div className="slr-card-title">{props.title}</div>}
                <AIModelSelector
                    studyId={studyId}
                    patientId={patientId}
                    imageId={null}
                    onExecutionStarted={(exec) => {
                        const modelName = exec?.model?.name || exec?.model_name || "model";
                        toast.info(`Running ${modelName}…`);
                    }}
                />
            </div>
        );
    }
    return <AIPanelBoundCard comp={comp} studyId={studyId} patientId={patientId} studyData={studyData} />;
};

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled", "error"]);

// ---- Screenshot helpers (used by AIPanelBoundCard) -------------------------

// Safari fallback when ImageCapture isn't available: mount a hidden <video>
// against the stream, wait for its first frame, return the video element so it
// can be drawn to a canvas (ctx.drawImage accepts video the same as ImageBitmap).
const grabFrameViaVideo = (stream) => new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
        video.play().then(() => {
            // One animation frame to ensure a frame is actually decoded.
            requestAnimationFrame(() => resolve(video));
        }).catch(reject);
    };
    video.onerror = reject;
});

// First visible imaging viewer iframe currently rendered by the runtime — the
// user is expected to navigate it to the slice they want before clicking
// Capture. Document-viewer iframes also use .slr-viewer-iframe; the
// data-slr-imaging="1" attribute on imaging viewers is what excludes docs.
const findVisibleViewerIframe = () => {
    const iframes = Array.from(document.querySelectorAll('iframe.slr-viewer-iframe[data-slr-imaging="1"]'));
    return iframes.find((el) => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
    }) || null;
};

// Crop the captured frame to the iframe's bounding rect and encode as a JPEG
// data URL. If no iframe is found, encode the whole frame.
//
// Scale is derived from the actual source dimensions vs the current viewport
// — NOT from window.devicePixelRatio — because getDisplayMedia on Chrome
// frequently hands back a downsampled stream (e.g. ~1080p on a retina display
// whose viewport × dpr would be much larger). Assuming dpr made every coord
// overshoot the source bounds, which the `Math.min(sourceH - sy, ...)` clamp
// then truncated — that's why earlier captures showed only the top sliver of
// the iframe.
const cropAndEncode = (source, iframe) => {
    const sourceW = source.videoWidth ?? source.width;
    const sourceH = source.videoHeight ?? source.height;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (iframe) {
        const r = iframe.getBoundingClientRect();
        const scaleX = sourceW / Math.max(1, window.innerWidth);
        const scaleY = sourceH / Math.max(1, window.innerHeight);
        const sx = Math.max(0, Math.round(r.left * scaleX));
        const sy = Math.max(0, Math.round(r.top * scaleY));
        const sw = Math.min(sourceW - sx, Math.round(r.width * scaleX));
        const sh = Math.min(sourceH - sy, Math.round(r.height * scaleY));
        if (sw > 0 && sh > 0) {
            canvas.width = sw;
            canvas.height = sh;
            ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
            return canvas.toDataURL("image/jpeg", 0.9);
        }
    }
    canvas.width = sourceW;
    canvas.height = sourceH;
    ctx.drawImage(source, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.9);
};

// (The old auto-pulling buildPatientContext was removed: patient_context is
// now constructed in AIPanelBoundCard.handleRun strictly from what the user
// explicitly selected in the Imaging screenshots + Text context sections.)

const AIPanelBoundCard = ({ comp, studyId, patientId, studyData }) => {
    const props = comp.props || {};
    const modelName = props.ai_model_name || "AI model";
    const schema = (props.parameters_schema && typeof props.parameters_schema === "object") ? props.parameters_schema : {};
    const modes = (props.param_modes && typeof props.param_modes === "object") ? props.param_modes : {};
    const presetValues = (props.param_values && typeof props.param_values === "object") ? props.param_values : {};

    // Local state for "editable" / "open" params. Fixed params don't render.
    const initialFormState = () => {
        const s = {};
        Object.entries(schema).forEach(([key, cfg]) => {
            const mode = modes[key] || "open";
            if (mode === "editable") s[key] = presetValues[key] ?? cfg?.default;
            else if (mode === "open") s[key] = cfg?.default;
            // fixed: kept in presetValues, not in form state
        });
        return s;
    };
    const [formValues, setFormValues] = useState(initialFormState);
    const [running, setRunning] = useState(false);
    // Every run the reviewer kicks off for this case is appended here so the
    // panel can show the full prompt-and-answer history (latest at the bottom).
    // Each entry is the execution record returned by the backend and then
    // updated in place by the polling effect until it reaches a terminal state.
    const [executions, setExecutions] = useState([]);
    // Researcher-configured cap on how many runs the reviewer can issue per
    // case. Missing/invalid → 1, matching the original single-run behavior.
    const maxRuns = (() => {
        const n = Number(props.max_runs);
        return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
    })();
    // The id of the in-flight execution (if any) — drives the polling effect
    // and the Run button's disabled state.
    const pendingExecutionId = (() => {
        const inFlight = executions.find((e) => !TERMINAL_STATUSES.has(String(e?.status || "").toLowerCase()));
        return inFlight?.id ?? null;
    })();

    // Imaging screenshots queued for the next model run. Each entry is
    // { name, data_url } — data_url is a "data:image/jpeg;base64,..." string
    // that the backend can drop straight into OpenAI's image_url field.
    const [screenshots, setScreenshots] = useState([]);
    // User-chosen text context entries: each is { name, content, kind } where
    // kind is "document" (text from a doc viewer) or "imaging_metadata" (a
    // formatted summary of the patient's imaging series). These are the ONLY
    // text snippets sent — nothing else is auto-pulled.
    const [textSnippets, setTextSnippets] = useState([]);
    // "Add Patient context" master toggle. When checked, the document
    // viewer's current text is auto-attached on Run so the reviewer doesn't
    // have to click "Add document text" manually, and any attached
    // screenshots / imaging metadata are forwarded too. When unchecked, no
    // patient_context payload is sent and the boolean flag in the
    // parameters is set to false — this prevents the user's chatgpt_api.py
    // script from receiving a truthy `patient_context` it can't introspect.
    const [includePatientContext, setIncludePatientContext] = useState(false);
    // Preview overlay state — click a screenshot or text pill to inspect the
    // exact payload that will be sent to the model.
    //   { kind: "image",   name, data_url }
    //   { kind: "text",    name, content, label }
    const [preview, setPreview] = useState(null);
    const SCREENSHOT_CAP = 10;

    useEffect(() => {
        // Re-initialize when patient changes — most params reset to defaults,
        // the previous case's execution result is no longer relevant, and the
        // queued screenshots / text snippets belonged to the previous patient.
        setFormValues(initialFormState());
        setExecutions([]);
        setScreenshots([]);
        setTextSnippets([]);
        setPreview(null);
        setIncludePatientContext(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, props.ai_model_id]);

    // ESC closes the preview overlay.
    useEffect(() => {
        if (!preview) return;
        const onKey = (e) => { if (e.key === "Escape") setPreview(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [preview]);

    // One-shot screen capture: prompt → grab one frame → close stream. The
    // browser's "sharing this tab" indicator disappears as soon as the frame
    // is captured. The user picks the tab once per capture.
    //
    // The OHIF iframe can be partially below the viewport fold inside the
    // scrollable layout. We scroll it fully into view before grabbing the
    // frame so the capture isn't truncated to whatever happened to be on
    // screen. The page is restored to its previous scroll position after.
    const captureFromDisplay = async () => {
        if (screenshots.length >= SCREENSHOT_CAP) {
            toast.warning(`Maximum ${SCREENSHOT_CAP} screenshots reached.`);
            return;
        }
        if (!navigator.mediaDevices?.getDisplayMedia) {
            toast.error("Screen capture isn't supported in this browser. Use the upload button instead.");
            return;
        }
        const iframe = findVisibleViewerIframe();
        if (!iframe) {
            toast.error("No imaging viewer is visible to capture.");
            return;
        }
        // Park the iframe at the top of the viewport so its full height is
        // on-screen (when it fits). `scrollIntoView` is synchronous in terms
        // of scrollTop changes but the new layout/paint isn't observable
        // until the next frame — so we wait two rAFs before kicking off
        // getDisplayMedia.
        const prevScroll = { x: window.scrollX, y: window.scrollY };
        iframe.scrollIntoView({ block: "start", inline: "nearest", behavior: "instant" });
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

        let stream;
        try {
            // Ask the OS for the largest stream it'll give us — without
            // constraints Chrome often delivers a downsampled (~1080p)
            // frame, which loses detail on high-DPR displays.
            const dpr = window.devicePixelRatio || 1;
            stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    width: { ideal: Math.round(window.innerWidth * dpr) },
                    height: { ideal: Math.round(window.innerHeight * dpr) },
                },
                audio: false,
                preferCurrentTab: true,
                selfBrowserSurface: "include",
            });
            const track = stream.getVideoTracks()[0];
            // ImageCapture isn't in Safari yet; fall back to a one-frame <video>.
            let source;
            if (window.ImageCapture) {
                source = await new ImageCapture(track).grabFrame();
            } else {
                source = await grabFrameViaVideo(stream);
            }

            // Re-query post-scroll: getBoundingClientRect inside cropAndEncode
            // reflects the position at the moment of the grab.
            const iframeNow = findVisibleViewerIframe() || iframe;
            const dataUrl = cropAndEncode(source, iframeNow);
            setScreenshots((prev) => [...prev, { name: `viewport_${prev.length + 1}`, data_url: dataUrl }]);
        } catch (err) {
            if (err && err.name === "NotAllowedError") return; // user cancelled
            console.error("[AI panel] screen capture failed:", err);
            toast.error("Screen capture failed. Try the upload button instead.");
        } finally {
            if (stream) stream.getTracks().forEach((t) => t.stop());
            window.scrollTo(prevScroll.x, prevScroll.y);
        }
    };

    const captureFromFile = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ""; // allow re-selecting the same file later
        if (files.length === 0) return;
        const remaining = SCREENSHOT_CAP - screenshots.length;
        if (remaining <= 0) {
            toast.warning(`Maximum ${SCREENSHOT_CAP} screenshots reached.`);
            return;
        }
        files.slice(0, remaining).forEach((file) => {
            const reader = new FileReader();
            reader.onload = () => {
                setScreenshots((prev) => prev.length < SCREENSHOT_CAP
                    ? [...prev, { name: file.name || `upload_${prev.length + 1}`, data_url: reader.result }]
                    : prev
                );
            };
            reader.readAsDataURL(file);
        });
    };

    const removeScreenshot = (idx) => {
        setScreenshots((prev) => prev.filter((_, i) => i !== idx));
    };

    // Grab the rendered text of every visible document viewer card on the page
    // and add each as a separate text-context entry. EmbeddedDocument tags
    // itself with data-slr-document so we can find it without coupling to its
    // internal state.
    // Scan the page for visible document viewers and return readable text
    // entries. Shared by the manual "Add document text" button and by Run
    // when "Add Patient context" is checked.
    const collectDocumentTextEntries = () => {
        const docCards = Array.from(document.querySelectorAll('.slr-viewer[data-slr-document="1"]'));
        const entries = [];
        for (const card of docCards) {
            const r = card.getBoundingClientRect();
            if (r.width <= 0 || r.height <= 0) continue;
            const name = (card.querySelector(".slr-viewer-toolbar span")?.textContent || "document viewer").trim();
            const contentEl = card.querySelector(".slr-document-content");
            // .slr-document-content is the markdown/text view. For iframe-rendered
            // docs (HTML/PDF) we can't read across the iframe, so we skip those
            // and ask the user to use Upload instead.
            const text = (contentEl?.textContent || "").trim();
            if (!text) continue;
            entries.push({ name, kind: "document", content: text });
        }
        return entries;
    };

    // The canonical "this is the model-facing context" document is named
    // `AI_model_context.*` per case. When present we always prefer it over
    // whatever happens to be the visible tab in the document viewer, so the
    // case-description doc the reviewer is reading doesn't get shipped to the
    // model accidentally and the model-context doc gets shipped even when its
    // tab isn't currently selected.
    const AI_MODEL_CONTEXT_PATTERN = /ai[_\- ]?model[_\- ]?context/i;

    const findAIModelContextDoc = () => {
        const patientItems = (studyData || []).filter(
            (item) => extractOriginalPatientId(item.patient_id) === patientId
        );
        const docs = patientItems.filter(
            (i) => i.data_type === "document" || i.source_model === "MedicalDocument"
        );
        return docs.find((d) => {
            const fname = (d.filename || d.file || d.location || "").toString();
            return AI_MODEL_CONTEXT_PATTERN.test(fname);
        }) || null;
    };

    const fetchDocumentContent = async (doc) => {
        const filename = (doc.filename || doc.file || doc.location || "").toString().split("/").pop() || "document";
        const ext = filename.toLowerCase().split(".").pop();
        const isText = ["txt", "md", "markdown", "csv", "tsv", "json", "log", "html", "htm"].includes(ext);
        if (!isText) {
            throw new Error(`"${filename}" is a binary document — text content isn't available.`);
        }
        const isPatientData = doc.source_model === "PatientData";
        const contentUrl = isPatientData
            ? (studyId
                ? `/studies/${studyId}/patient-data/${doc.id}/content/`
                : `/data_manager/patient-data/${doc.id}/content/`)
            : `/data_manager/medical-documents/${doc.id}/content/`;
        const resp = await API.get(contentUrl);
        return { name: filename, content: resp?.data?.content || "" };
    };

    const addDocumentText = async () => {
        // Prefer the AI_model_context doc if the patient has one. This doc is
        // authored specifically for model consumption and may not be the tab
        // the reviewer is currently looking at.
        const aiDoc = findAIModelContextDoc();
        if (aiDoc) {
            try {
                const { name, content } = await fetchDocumentContent(aiDoc);
                if (!content.trim()) {
                    toast.warning(`Found "${name}" but it's empty.`);
                    return;
                }
                setTextSnippets((prev) => {
                    if (prev.some((d) => d.content === content)) return prev;
                    return [...prev, { name, kind: "document", content }];
                });
                return;
            } catch (err) {
                console.warn("[ScrollableRenderer] AI_model_context fetch failed:", err);
                toast.warning(
                    err?.message
                        ? `Couldn't load AI_model_context doc: ${err.message}. Falling back to the visible document.`
                        : "Couldn't load AI_model_context doc. Falling back to the visible document."
                );
                // Fall through to the visible-card scan below.
            }
        }

        const newEntries = collectDocumentTextEntries();
        if (newEntries.length === 0) {
            // Distinguish "no viewer at all" from "viewer exists but unreadable".
            const anyDocViewer = document.querySelector('.slr-viewer[data-slr-document="1"]');
            if (!anyDocViewer) {
                toast.warning("No document viewer found in this layout.");
            } else {
                toast.warning("No readable text in the current document viewer (binary docs aren't supported here — use Upload).");
            }
            return;
        }
        setTextSnippets((prev) => {
            const seen = new Set(prev.map((d) => d.content));
            return [...prev, ...newEntries.filter((d) => !seen.has(d.content))];
        });
    };

    // Add a structured summary of the patient's imaging series as a text
    // snippet. Useful for text-only models that can't accept the screenshots
    // directly but should still know what imaging exists.
    const addImagingMetadata = () => {
        const items = (studyData || []).filter(
            (i) => extractOriginalPatientId(i.patient_id) === patientId
        );
        const imagingItems = items.filter(isDicomItem);
        if (imagingItems.length === 0) {
            toast.warning("No imaging found for this patient.");
            return;
        }
        const seenSeries = new Set();
        const lines = [];
        const modalities = new Set();
        for (const it of imagingItems) {
            const seriesKey = it.series_id || it.metadata?.dicom_structure?.series_id || `${it.id}`;
            if (seenSeries.has(seriesKey)) continue;
            seenSeries.add(seriesKey);
            const mod = (it.modality || "").toUpperCase();
            const ft = it.original_file_type || "";
            const sn = it.series_name || it.metadata?.dicom_structure?.series_description || "";
            if (mod) modalities.add(mod);
            const parts = [];
            if (mod) parts.push(`${mod}`);
            if (ft) parts.push(`format ${ft}`);
            if (sn) parts.push(`series "${sn}"`);
            lines.push(`- ${parts.join(", ") || "(unnamed series)"}`);
        }
        const content = `Patient: ${patientId}\n`
            + `Modalities: ${Array.from(modalities).join(", ") || "(none)"}\n`
            + `Series count: ${lines.length}\n\n`
            + lines.join("\n");
        setTextSnippets((prev) => {
            const seen = new Set(prev.map((d) => d.content));
            if (seen.has(content)) return prev;
            return [...prev, { name: "Imaging metadata", kind: "imaging_metadata", content }];
        });
    };

    const removeTextSnippet = (idx) => {
        setTextSnippets((prev) => prev.filter((_, i) => i !== idx));
    };

    // Poll the in-flight execution (if any) until it reaches a terminal state.
    // The polled id is captured via pendingExecutionId so this effect only
    // re-subscribes when a different run starts — not on every status update.
    // We also fire a toast on the terminal transition so the reviewer gets
    // explicit feedback for failures and empty answers instead of the panel
    // quietly showing a Query with no Answer block beneath it.
    useEffect(() => {
        if (!pendingExecutionId) return;
        let cancelled = false;
        const id = pendingExecutionId;
        const interval = setInterval(async () => {
            try {
                const updated = await getExecutionStatus(id);
                if (cancelled) return;
                setExecutions((prev) => prev.map((e) => (e.id === id ? updated : e)));
                const newStatus = String(updated.status || "").toLowerCase();
                if (TERMINAL_STATUSES.has(newStatus)) {
                    clearInterval(interval);
                    if (newStatus === "failed" || newStatus === "error" || newStatus === "cancelled") {
                        const detail = updated.error_message
                            || `Execution ${newStatus}. Check the model logs for details.`;
                        toast.error(`${modelName} failed: ${detail}`, { autoClose: 8000 });
                    } else if (newStatus === "completed") {
                        // "Completed" with no answer / citations is the silent-
                        // failure case (LLM truncation, content filter, empty
                        // [Response_Start]…[Response_End] block, etc). Tell the
                        // reviewer explicitly so they don't think the panel is
                        // still loading or that they missed the answer.
                        const text = updated.text_output;
                        let answer = "";
                        let citations = [];
                        if (text && typeof text === "object") {
                            answer = typeof text.answer === "string" ? text.answer : "";
                            citations = Array.isArray(text.citations) ? text.citations : [];
                        } else if (typeof text === "string") {
                            answer = text;
                        }
                        if (!answer.trim() && citations.length === 0) {
                            toast.warning(
                                `${modelName} returned an empty response. Try rephrasing the question, attaching patient context, or running it again.`,
                                { autoClose: 8000 }
                            );
                        }
                    }
                }
            } catch (err) {
                console.error("Execution poll failed:", err);
            }
        }, 2000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [pendingExecutionId, modelName]);

    const setField = (key, value) => setFormValues((s) => ({ ...s, [key]: value }));

    // Schema keys whose schema-driven form input we suppress because the
    // reviewer-facing "Add Patient context" checkbox manages them. We keep
    // both names so older parameter schemas that used either spelling still
    // resolve to the same checkbox.
    const INCLUDE_CONTEXT_FLAG_KEYS = ["include_patient_context", "include_context"];
    // Keys whose value is the patient_context payload itself (a dict), not a
    // boolean. Suppressed from the form too — the reviewer never types this
    // by hand; it's assembled from the attachments below.
    const PATIENT_CONTEXT_DATA_KEYS = ["patient_context"];
    const INCLUDE_CONTEXT_KEYS = [...INCLUDE_CONTEXT_FLAG_KEYS, ...PATIENT_CONTEXT_DATA_KEYS];

    // Researcher-configured set of context kinds this panel is allowed to
    // collect. A missing prop means "all kinds" — matches the behavior of
    // panels that existed before this option was added.
    const ALL_CONTEXT_KINDS = ["images", "documents", "imaging_metadata"];
    const enabledKinds = Array.isArray(props.context_kinds) ? props.context_kinds : ALL_CONTEXT_KINDS;
    const allowImages = enabledKinds.includes("images");
    const allowDocuments = enabledKinds.includes("documents");
    const allowImagingMeta = enabledKinds.includes("imaging_metadata");
    const allowAnyText = allowDocuments || allowImagingMeta;

    const handleRun = async () => {
        if (!patientId) {
            toast.error("No patient selected.");
            return;
        }
        if (executions.length >= maxRuns) {
            toast.warning(`Maximum ${maxRuns} run${maxRuns === 1 ? "" : "s"} reached for this case.`);
            return;
        }
        if (pendingExecutionId) {
            toast.warning("A run is already in progress.");
            return;
        }
        // Merge fixed values + user-provided values
        const parameters = {};
        Object.entries(schema).forEach(([key]) => {
            const mode = modes[key] || "open";
            if (mode === "fixed") parameters[key] = presetValues[key];
            else parameters[key] = formValues[key];
        });

        setRunning(true);
        try {
            // patient_context is gated by the "Add Patient context"
            // checkbox. When unchecked we send nothing patient-shaped — the
            // boolean flag goes out as false and the dict key is removed
            // entirely (older runtimes incorrectly sent the flag's boolean
            // under the dict key, which crashed scripts that expected a real
            // mapping). When checked, the document text is auto-attached
            // (matching the legacy checkbox UX) and any manually-attached
            // screenshots / metadata are forwarded as well.
            let effectiveSnippets = textSnippets;
            if (includePatientContext) {
                // Prefer the AI_model_context doc: each patient may carry
                // an authored-for-the-model doc (filename includes
                // "AI_model_context") that is not necessarily the tab the
                // reviewer is reading. When that doc exists, we ship its
                // content instead of whichever document the document-viewer
                // happens to have visible. Falls back to the visible-card
                // scan when no such doc exists for this patient.
                let autoDocs = [];
                const aiDoc = findAIModelContextDoc();
                if (aiDoc) {
                    try {
                        const { name, content } = await fetchDocumentContent(aiDoc);
                        if (content.trim()) {
                            autoDocs = [{ name, kind: "document", content }];
                        }
                    } catch (err) {
                        console.warn("[ScrollableRenderer] AI_model_context fetch failed:", err);
                        // Silent fall-through to visible-card scan; the
                        // Run isn't worth blocking on this.
                    }
                }
                if (autoDocs.length === 0) {
                    autoDocs = collectDocumentTextEntries();
                }
                if (autoDocs.length > 0) {
                    const seen = new Set(textSnippets.map((d) => d.content));
                    const merged = [
                        ...textSnippets,
                        ...autoDocs.filter((d) => !seen.has(d.content)),
                    ];
                    effectiveSnippets = merged;
                    // Surface the auto-attached doc so the reviewer can
                    // see/preview/remove it after the run.
                    if (merged.length !== textSnippets.length) setTextSnippets(merged);
                }
            }

            const ctx = {};
            if (includePatientContext) {
                if (screenshots.length > 0) ctx.images = screenshots;
                const docs = effectiveSnippets.filter((d) => d.kind === "document");
                if (docs.length > 0) {
                    ctx.documents = docs.map(({ name, content }) => ({ name, content }));
                }
                const meta = effectiveSnippets.filter((d) => d.kind === "imaging_metadata");
                if (meta.length > 0) {
                    ctx.imaging_metadata = meta.map(({ name, content }) => ({ name, content }));
                }
            }

            // Remove any schema-default patient_context value so it never
            // travels as a stray boolean / placeholder. We set it back as a
            // dict below only when the checkbox is on and we have content.
            for (const k of PATIENT_CONTEXT_DATA_KEYS) {
                if (k in parameters) delete parameters[k];
            }
            if (includePatientContext && Object.keys(ctx).length > 0) {
                parameters.patient_context = ctx;
            }
            // The boolean flag mirrors the checkbox so script branches like
            // `if include_patient_context: ...` stay aligned with what's sent.
            for (const k of INCLUDE_CONTEXT_FLAG_KEYS) {
                if (k in parameters) parameters[k] = includePatientContext;
            }

            // Stitch the reviewer's prior turns in this case onto the new
            // prompt so follow-up questions resolve pronouns / build on
            // earlier answers ("tell me more about it") instead of each run
            // starting from a blank slate. The case scope is enforced by the
            // patientId-keyed reset of `executions` above.
            const priorTurns = [];
            for (const prev of executions) {
                if (String(prev?.status || "").toLowerCase() !== "completed") continue;
                const out = prev?.text_output;
                const prevUserQuery = prev?.parameters?.user_query
                    ?? prev?.parameters?.query
                    ?? (out && typeof out === "object" ? out.query : null);
                const prevAnswer = out && typeof out === "object"
                    ? out.answer
                    : (typeof out === "string" ? out : null);
                if (!prevUserQuery || !prevAnswer) continue;
                priorTurns.push({ user: String(prevUserQuery), assistant: String(prevAnswer) });
            }
            if (priorTurns.length > 0) {
                parameters.conversation_history = priorTurns.flatMap(({ user, assistant }) => [
                    { role: "user", content: user },
                    { role: "assistant", content: assistant },
                ]);
                // Model-agnostic fallback: also fold the transcript into the
                // primary `query` so QA models that only read `query` still
                // see the prior turns. Keep the reviewer's clean prompt in
                // `user_query` for the UI to display.
                if (typeof parameters.query === "string" && parameters.query.trim()) {
                    const transcript = priorTurns
                        .map(({ user, assistant }, i) => `Q${i + 1}: ${user}\nA${i + 1}: ${assistant}`)
                        .join("\n\n");
                    parameters.user_query = parameters.query;
                    parameters.query =
                        `Previous conversation in this case:\n${transcript}\n\n` +
                        `Current question: ${parameters.query}`;
                }
            }

            const exec = await runModel(props.ai_model_id, {
                patient_id: patientId,
                study_id: studyId,
                parameters,
            });
            if (exec && exec.id) {
                setExecutions((prev) => [...prev, exec]);
            }
            toast.info(`Running ${modelName}…`);
        } catch (err) {
            console.error("AI panel run failed:", err);
            // A 404 here almost always means the model the AI panel was
            // configured with has been deleted or re-uploaded (new PK).
            // Steer the user toward the fix instead of just echoing the
            // generic backend message.
            if (err?.response?.status === 404) {
                toast.error(
                    `Model "${modelName}" was not found (id ${props.ai_model_id}). ` +
                    `Open this viewer in the Viewer Creation Lab and re-select the AI model.`,
                    { autoClose: 8000 }
                );
            } else {
                toast.error(err?.response?.data?.error || `Failed to run ${modelName}.`);
            }
        } finally {
            setRunning(false);
        }
    };

    const renderField = (key, cfg) => {
        const mode = String(modes[key] || "open").toLowerCase();
        // Researcher-locked params never render — the reviewer only sees inputs
        // they're allowed to touch.
        if (mode === "fixed" || mode === "locked" || mode === "hidden") return null;
        // The "Include patient context" boolean is no longer reviewer-facing:
        // the context attachment sections below are always visible, and we
        // set the flag automatically based on whether the reviewer attached
        // anything. Skip rendering an input for it so it doesn't appear as a
        // stray checkbox above the attachment widgets.
        if (INCLUDE_CONTEXT_KEYS.includes(key)) return null;
        const v = formValues[key];
        const label = cfg?.label || key;
        const onChange = (next) => setField(key, next);
        const common = { id: `ai-${key}` };
        let input;
        switch (cfg?.type) {
            case "boolean":
                input = (
                    <label className="reveal-checkbox">
                        <input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)} />
                        <span>{label}</span>
                    </label>
                );
                break;
            case "select":
                input = (
                    <select className="form-select" value={v ?? ""} onChange={(e) => onChange(e.target.value)} {...common}>
                        <option value="">Select...</option>
                        {(cfg.options || []).map((opt) => {
                            const ov = typeof opt === "object" ? opt.value : opt;
                            const ol = typeof opt === "object" ? opt.label : opt;
                            return <option key={ov} value={ov}>{ol}</option>;
                        })}
                    </select>
                );
                break;
            case "number":
                input = (
                    <input
                        type="number"
                        className="form-control"
                        value={v ?? ""}
                        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                        {...common}
                    />
                );
                break;
            case "textarea":
                input = (
                    <textarea
                        className="form-control"
                        rows={3}
                        value={v ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        {...common}
                    />
                );
                break;
            default:
                input = (
                    <input
                        type="text"
                        className="form-control"
                        value={v ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        {...common}
                    />
                );
        }
        return (
            <div key={key} className="slr-ai-field" style={{ marginBottom: 10 }}>
                {cfg?.type !== "boolean" && (
                    <label htmlFor={`ai-${key}`} className="slr-card-title" style={{ display: "block", marginBottom: 4 }}>{label}</label>
                )}
                {input}
                {cfg?.description && <div className="text-muted small" style={{ marginTop: 2 }}>{cfg.description}</div>}
            </div>
        );
    };

    return (
        <div className="slr-card slr-ai-panel">
            {props.title && <div className="slr-card-title"><i className="fa fa-robot me-2"></i>{props.title}</div>}
            {Object.entries(schema).map(([key, cfg]) => renderField(key, cfg))}

            {/* "Add Patient context" master checkbox. Off by default — keeps
                runs lean. When on, the document viewer's current text is
                auto-attached on Run and any manually-attached screenshots /
                imaging metadata also travel with the request. */}
            <div className="slr-ai-field" style={{ marginBottom: 10 }}>
                <label className="reveal-checkbox">
                    <input
                        type="checkbox"
                        checked={includePatientContext}
                        onChange={(e) => setIncludePatientContext(e.target.checked)}
                    />
                    <span>Add Patient context</span>
                </label>
                <div className="text-muted small" style={{ marginTop: 2 }}>
                    When enabled, the visible document viewer's text is sent
                    to the model along with any imaging screenshots or
                    metadata attached below.
                </div>
            </div>

            {allowImages && (
                    <div className="slr-ai-screenshots">
                        <div className="slr-card-title" style={{ marginBottom: 4 }}>
                            Imaging screenshots {screenshots.length > 0 && `(${screenshots.length}/${SCREENSHOT_CAP})`}
                        </div>
                        <div className="slr-ai-screenshots-hint">
                            Capture up to {SCREENSHOT_CAP} viewer screenshots to send to the model. Navigate to the slice you want first, then click Capture.
                        </div>
                        <div className="slr-ai-screenshots-actions">
                            <button
                                type="button"
                                className="btn btn-outline-light btn-sm"
                                onClick={captureFromDisplay}
                                disabled={screenshots.length >= SCREENSHOT_CAP}
                            >
                                <i className="fa fa-camera me-1"></i> Capture viewer
                            </button>
                            <label className={`btn btn-outline-light btn-sm ${screenshots.length >= SCREENSHOT_CAP ? "disabled" : ""}`}>
                                <i className="fa fa-upload me-1"></i> Upload
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={captureFromFile}
                                    disabled={screenshots.length >= SCREENSHOT_CAP}
                                    style={{ display: "none" }}
                                />
                            </label>
                        </div>
                        {screenshots.length > 0 && (
                            <div className="slr-ai-screenshots-grid">
                                {screenshots.map((shot, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="slr-ai-screenshot-thumb slr-ai-thumb-button"
                                        title="Click to preview"
                                        onClick={() => setPreview({
                                            kind: "image",
                                            name: shot.name,
                                            data_url: shot.data_url,
                                        })}
                                    >
                                        <img src={shot.data_url} alt={shot.name} />
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            className="slr-ai-screenshot-remove"
                                            title="Remove"
                                            onClick={(e) => { e.stopPropagation(); removeScreenshot(idx); }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    removeScreenshot(idx);
                                                }
                                            }}
                                        >
                                            <i className="fa fa-times"></i>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
            )}

            {/* Text context section intentionally hidden: this project drives
                document-text inclusion entirely through the "Add Patient
                context" checkbox above, which auto-attaches the visible
                document viewer's text on Run. The manual Add document text /
                Add imaging metadata controls and the snippet pill row are
                kept in code (collectDocumentTextEntries / addDocumentText /
                addImagingMetadata) so the auto-attach path still works. */}

            {preview && (
                <div
                    className="slr-ai-preview-overlay"
                    onClick={() => setPreview(null)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        className="slr-ai-preview-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="slr-ai-preview-header">
                            <i className={`fa ${preview.kind === "image" ? "fa-image" : "fa-file-text"} me-2`}></i>
                            <span className="slr-ai-preview-title">
                                {preview.kind === "text" && (
                                    <span className="slr-ai-preview-label">{preview.label}: </span>
                                )}
                                {preview.name}
                            </span>
                            <button
                                type="button"
                                className="slr-ai-preview-close"
                                title="Close"
                                onClick={() => setPreview(null)}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                        <div className="slr-ai-preview-body">
                            {preview.kind === "image" ? (
                                <img
                                    src={preview.data_url}
                                    alt={preview.name}
                                    className="slr-ai-preview-image"
                                />
                            ) : (
                                <pre className="slr-ai-preview-text">{preview.content}</pre>
                            )}
                        </div>
                        <div className="slr-ai-preview-footer">
                            {preview.kind === "image"
                                ? "This is the exact image that will be sent to the model."
                                : `${preview.content.length.toLocaleString()} characters — this is the exact text that will be sent to the model.`}
                        </div>
                    </div>
                </div>
            )}

            <button
                type="button"
                className="btn btn-primary w-100"
                onClick={handleRun}
                disabled={running || !!pendingExecutionId || executions.length >= maxRuns}
            >
                {running || pendingExecutionId
                    ? <>Running…</>
                    : executions.length >= maxRuns
                        ? <><i className="fa fa-check me-2"></i>Max runs reached ({maxRuns}/{maxRuns})</>
                        : (
                            <>
                                <i className="fa fa-play me-2"></i>
                                Run model
                                {maxRuns > 1 && executions.length > 0 && (
                                    <span style={{ marginLeft: 6, opacity: 0.85 }}>
                                        ({executions.length}/{maxRuns})
                                    </span>
                                )}
                            </>
                        )}
            </button>
            {executions.length > 0 && (
                <div className="slr-ai-results-list">
                    {executions.map((exec, idx) => (
                        <AIPanelResult
                            key={exec.id ?? idx}
                            execution={exec}
                            runNumber={idx + 1}
                            totalRuns={executions.length}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Lightweight result display for an AI execution kicked off by the AI panel.
// We deliberately surface only the user's `query` and the model's `answer` —
// the rest of the text_output payload (model_used, tokens, temperature,
// elapsed_seconds, patient_context summary, …) is operational metadata that
// clutters the reviewer's view. It's still stored on the execution record
// for audit/debug purposes.
const AIPanelResult = ({ execution, onDismiss, runNumber, totalRuns }) => {
    const status = String(execution?.status || "running").toLowerCase();
    const isTerminal = TERMINAL_STATUSES.has(status);
    const isFailed = status === "failed" || status === "error" || status === "cancelled";

    const renderBody = () => {
        if (!isTerminal) {
            return (
                <div className="slr-ai-result-running">
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    {status === "queued" || status === "pending" ? "Queued…" : "Running…"}
                </div>
            );
        }
        if (isFailed) {
            return (
                <div className="slr-ai-result-error">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    {execution.error_message || `Execution ${status}.`}
                </div>
            );
        }
        // Completed — extract query + answer from whatever shape text_output is.
        const text = execution.text_output;
        let query;
        let answer;
        let citations;
        if (text && typeof text === "object") {
            query = text.query;
            answer = text.answer;
            if (Array.isArray(text.citations) && text.citations.length > 0) {
                citations = text.citations;
            }
        } else if (typeof text === "string") {
            answer = text;
        }
        // For follow-up turns the prompt we sent has the prior transcript
        // prepended (see handleRun) — display the reviewer's actual typed
        // question instead of the enriched payload.
        const cleanUserQuery = execution?.parameters?.user_query;
        if (cleanUserQuery) query = cleanUserQuery;
        // Fall back to the parameters we submitted if the model didn't echo the
        // query back (some QA models don't include it in their result).
        if (!query) query = execution?.parameters?.query;

        // "Completed" but the model returned nothing the reviewer can read.
        // Could be: LLM truncation (reasoning-token budget on GPT-5/o-series),
        // a content filter, or an empty [Response_Start][Response_End] block.
        // Show the question they asked plus an explicit warning instead of a
        // silent panel.
        const hasAnswer = typeof answer === "string" && answer.trim().length > 0;
        const hasCitations = Array.isArray(citations) && citations.length > 0;
        const isEmpty = !hasAnswer && !hasCitations;
        if (isEmpty) {
            return (
                <div className="slr-ai-result-body">
                    {query && (
                        <div className="slr-ai-result-section">
                            <div className="slr-ai-result-label">Query</div>
                            <div className="slr-ai-result-value">
                                <div style={{ whiteSpace: "pre-wrap" }}>{query}</div>
                            </div>
                        </div>
                    )}
                    <div className="slr-ai-result-error">
                        <i className="fa fa-exclamation-triangle me-2"></i>
                        The model returned an empty response. This usually means it
                        hit a token limit, ran into a content filter, or couldn't
                        find anything relevant — try rephrasing the question,
                        attaching patient context, or running it again.
                    </div>
                </div>
            );
        }

        // Stable per-result prefix so multiple runs' citation anchors don't
        // clash if more than one is mounted at once.
        const anchorPrefix = `cite-${execution?.id ?? Math.random().toString(36).slice(2, 8)}-`;

        // OpenScholar inlines `[0]`, `[1]`, … in the answer to reference the
        // citations array by index. Rewrite them to markdown links pointing at
        // the anchor we emit below; we'll handle the link click ourselves to
        // smooth-scroll within the panel instead of changing the URL hash.
        const renderedAnswer = answer && citations
            ? answer.replace(/\[(\d+)\]/g, (match, idx) => {
                const i = Number(idx);
                if (Number.isInteger(i) && i >= 0 && i < citations.length) {
                    return `[\\[${i}\\]](#${anchorPrefix}${i})`;
                }
                return match;
            })
            : answer;

        const onCitationLinkClick = (e, href) => {
            if (!href || !href.startsWith(`#${anchorPrefix}`)) return;
            e.preventDefault();
            const target = document.getElementById(href.slice(1));
            if (target) {
                target.scrollIntoView({ behavior: "smooth", block: "center" });
                target.classList.add("slr-ai-citation--flash");
                setTimeout(() => target.classList.remove("slr-ai-citation--flash"), 1400);
            }
        };

        return (
            <div className="slr-ai-result-body">
                {query && (
                    <div className="slr-ai-result-section">
                        <div className="slr-ai-result-label">Query</div>
                        <div className="slr-ai-result-value">
                            <div style={{ whiteSpace: "pre-wrap" }}>{query}</div>
                        </div>
                    </div>
                )}
                {answer && (
                    <div className="slr-ai-result-section">
                        <div className="slr-ai-result-label">Answer</div>
                        <div className="slr-ai-result-value">
                            <ReactMarkdown
                                components={{
                                    a: ({ node, href, children, ...rest }) => {
                                        const isInline = href && href.startsWith(`#${anchorPrefix}`);
                                        return (
                                            <a
                                                href={href}
                                                onClick={isInline ? (e) => onCitationLinkClick(e, href) : undefined}
                                                target={isInline ? undefined : "_blank"}
                                                rel={isInline ? undefined : "noopener noreferrer"}
                                                className={isInline ? "slr-ai-citation-ref" : undefined}
                                                {...rest}
                                            >
                                                {children}
                                            </a>
                                        );
                                    },
                                }}
                            >
                                {renderedAnswer}
                            </ReactMarkdown>
                        </div>
                    </div>
                )}
                {citations && (
                    <div className="slr-ai-result-section">
                        <div className="slr-ai-result-label">
                            Citations ({citations.length})
                        </div>
                        <ol className="slr-ai-citations">
                            {citations.map((c, i) => {
                                const idx = Number.isInteger(c?.index) ? c.index : i;
                                const href = c?.url || "";
                                const title = c?.title || "Untitled";
                                const authors = c?.authors || "";
                                const year = c?.year ?? null;
                                const cc = c?.citation_count;
                                return (
                                    <li
                                        key={`${anchorPrefix}${idx}`}
                                        id={`${anchorPrefix}${idx}`}
                                        className="slr-ai-citation"
                                        value={idx}
                                    >
                                        <div className="slr-ai-citation-title">
                                            {href ? (
                                                <a
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={href}
                                                >
                                                    {title}
                                                    <i className="fa fa-external-link ms-1" style={{ fontSize: 11, opacity: 0.7 }}></i>
                                                </a>
                                            ) : (
                                                <span>{title}</span>
                                            )}
                                        </div>
                                        <div className="slr-ai-citation-meta">
                                            {authors && <span>{authors}</span>}
                                            {year && <span> · {year}</span>}
                                            {Number.isFinite(cc) && cc > 0 && (
                                                <span> · {cc} citation{cc === 1 ? "" : "s"}</span>
                                            )}
                                        </div>
                                        {c?.abstract && (
                                            <details className="slr-ai-citation-abstract">
                                                <summary>Abstract</summary>
                                                <div>{c.abstract}</div>
                                            </details>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`slr-ai-result ${isFailed ? "is-error" : isTerminal ? "is-done" : "is-running"}`}>
            <div className="slr-ai-result-header">
                <span>
                    <i className="fa fa-flask me-2"></i>
                    Result
                    {totalRuns > 1 && (
                        <span className="slr-ai-result-counter">
                            {" "}#{runNumber}/{totalRuns}
                        </span>
                    )}
                </span>
                {/* Result cards are intentionally not dismissable in the runtime:
                    closing them would drop the run from `executions` and let the
                    reviewer recoup a run slot, effectively bypassing the
                    researcher-configured `max_runs` cap. */}
            </div>
            {renderBody()}
        </div>
    );
};

// Small helper: red asterisk shown next to labels for fields marked required
// by at least one submit button. `invalid` also toggles a red-border class on
// the input to flag the specific field the last blocked submit was waiting on.
const RequiredAsterisk = () => (
    <span className="slr-required-asterisk" title="Required" aria-label="required">*</span>
);

const SliderField = ({ comp, value, onChange, locked, required, invalid }) => {
    const min = comp.props?.min ?? 0;
    const max = comp.props?.max ?? 100;
    const current = value ?? comp.props?.value ?? Math.round((min + max) / 2);
    // A slider is "answered" only after it has been written to formState. An
    // untouched required slider therefore reads as empty — same as an unfilled
    // text field — and blocks the submit. This handler commits the currently-
    // displayed value on the first pointer or keyboard interaction so the
    // reviewer can accept the default with a single click instead of having
    // to drag away and back.
    const commitOnFirstTouch = () => {
        if (value === undefined || value === null) onChange(current);
    };
    return (
        <div className={`slr-card${locked ? " slr-locked" : ""}${invalid ? " slr-field-invalid" : ""}`}>
            <div className="slr-card-title">
                {comp.props?.label || "Slider"}
                {required && <RequiredAsterisk />}
                : <span className="slr-slider-value">{current}</span>
                {locked && <i className="fa fa-lock ms-2" title="Locked after submit" style={{ opacity: 0.7 }}></i>}
            </div>
            <input
                type="range"
                min={min}
                max={max}
                value={current}
                disabled={locked}
                className="form-range"
                onPointerDown={commitOnFirstTouch}
                onKeyDown={commitOnFirstTouch}
                onChange={(e) => onChange(Number(e.target.value))}
            />
        </div>
    );
};

const InputField = ({ comp, value, onChange, locked, required, invalid }) => (
    <div className={`slr-card${locked ? " slr-locked" : ""}${invalid ? " slr-field-invalid" : ""}`}>
        {comp.props?.label && (
            <div className="slr-card-title">
                {comp.props.label}
                {required && <RequiredAsterisk />}
                {locked && <i className="fa fa-lock ms-2" title="Locked after submit" style={{ opacity: 0.7 }}></i>}
            </div>
        )}
        <input
            type="text"
            className="form-control"
            placeholder={comp.props?.placeholder || ""}
            value={value || ""}
            readOnly={locked}
            onChange={(e) => onChange(e.target.value)}
        />
    </div>
);

const TextareaField = ({ comp, value, onChange, locked, required, invalid }) => (
    <div className={`slr-card${locked ? " slr-locked" : ""}${invalid ? " slr-field-invalid" : ""}`}>
        {comp.props?.label && (
            <div className="slr-card-title">
                {comp.props.label}
                {required && <RequiredAsterisk />}
                {locked && <i className="fa fa-lock ms-2" title="Locked after submit" style={{ opacity: 0.7 }}></i>}
            </div>
        )}
        <textarea
            className="form-control"
            placeholder={comp.props?.placeholder || ""}
            rows={comp.props?.rows || 3}
            value={value || ""}
            readOnly={locked}
            onChange={(e) => onChange(e.target.value)}
        />
    </div>
);

const SubmitButton = ({ comp, onSubmit, submitted }) => (
    <div className="slr-card">
        <button
            type="button"
            className="btn btn-info w-100"
            onClick={onSubmit}
            disabled={submitted}
        >
            {submitted ? `Submitted — ${comp.props?.label || "OK"}` : comp.props?.label || "Submit"}
        </button>
    </div>
);

const NavButtons = ({ comp, queueIndex, queueLength, onPrev, onNext }) => {
    const atStart = queueIndex <= 0;
    const atEnd = queueIndex < 0 || queueIndex >= queueLength - 1;
    const forwardOnly = comp?.props?.direction === "forward";
    return (
        <div className="slr-card slr-nav">
            {!forwardOnly && (
                <button type="button" className="btn btn-outline-light" onClick={onPrev} disabled={atStart}>
                    <i className="fa fa-arrow-left me-2"></i>Previous case
                </button>
            )}
            <div className="slr-nav-progress">
                {queueIndex >= 0 ? `${queueIndex + 1} / ${queueLength}` : "—"}
            </div>
            <button type="button" className="btn btn-primary" onClick={onNext}>
                {atEnd ? "Finish" : "Next case"}<i className="fa fa-arrow-right ms-2"></i>
            </button>
        </div>
    );
};

// Extract the original patient ID from a study/user-isolated copy. Mirrors
// api/dv_viewers/views.py::_extract_original_patient_id.
const extractOriginalPatientId = (pid) => {
    if (!pid) return pid;
    const um = pid.match(/^(.+?)_s(\d+)_([a-zA-Z0-9_]+)$/);
    if (um) return um[1];
    const sm = pid.match(/^(.+?)_study(\d+)$/);
    if (sm) return sm[1];
    return pid;
};

// Mirrors the DICOM filter in StudyDashboard's ViewerCard handleLaunchViewer
// (the "standard" OHIF launch). Accepts native DICOM, NIfTI (converted server-
// side), image-slice imaging (jpg/png inside imaging — also converted), and
// MedicalImage rows. Too-narrow filtering was rejecting cases like
// case_01_tracheal_lymphoma whose imaging is stored as NIfTI.
const isDicomItem = (item) => {
    if (!item) return false;
    if (item.data_type === "document") return false;

    // MedicalImage rows are always treated as imaging by the OHIF launch path.
    if (item.source_model === "MedicalImage") return true;

    const filePath = (item.image || item.file || item.location || "").toLowerCase();
    const regularImageExt = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".tiff", ".tif", ".webp"];
    const isRegularImage = regularImageExt.some((ext) => filePath.endsWith(ext));

    const isImagingDicom = item.data_type === "imaging" && item.original_file_type === "dicom";
    const isNiftiConverted = item.data_type === "imaging" && item.original_file_type === "nifti";
    const isImagingJpgPng = item.data_type === "imaging" &&
        ["jpg", "jpeg", "png"].includes(item.original_file_type);
    const isDcmExtension = filePath.endsWith(".dcm");
    const isDicomPath =
        filePath.includes("/dicom") ||
        filePath.includes("nifti_to_dicom") ||
        filePath.includes("rotated_") ||
        filePath.includes("/nifti/");
    const typeContainsDicom = item.type?.toLowerCase().includes("dicom");
    const modality = (item.modality || "").toUpperCase();
    const dicomModalities = ["CT", "MR", "MRI", "X-RAY", "PET", "ULTRASOUND", "US", "NM", "CR", "DX", "MG", "PT", "SEG", "SC"];
    const hasDicomModality = dicomModalities.includes(modality);
    const hasMedicalImageId = !!item.metadata?.medical_image_id;

    // Imaging jpg/png bypass the regular-image extension reject (server converts them).
    if (isImagingJpgPng) return true;
    if (isRegularImage) return false;
    return isImagingDicom || isNiftiConverted || isDcmExtension || isDicomPath ||
        typeContainsDicom || hasDicomModality || hasMedicalImageId;
};

// Categorize a patient's items by what each viewer kind needs. Same partitioning
// as StudyDashboard's reader-queue dispatcher (kept in sync; we duplicate here
// rather than cross-import to avoid a circular dependency through DvViewer).
const splitPatientItems = (items) => {
    const dicom = { medicalImageIds: [], patientDataIds: [] };
    const regularImages = [];
    const documents = [];
    const signals = [];
    const ehr = [];
    const pathology = [];
    const dicomItems = [];
    const regularImageExt = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".tiff", ".tif", ".webp"];

    for (const item of items) {
        if (item.data_type === "document") { documents.push(item); continue; }
        if (item.data_type === "signal") { signals.push(item); continue; }
        if (item.data_type === "ehr") { ehr.push(item); continue; }
        if (item.data_type === "pathology") { pathology.push(item); continue; }
        if (isDicomItem(item)) {
            dicomItems.push(item);
            if (item.source_model === "MedicalImage") {
                dicom.medicalImageIds.push(item.id);
            }
            // Mirror the standard launch: a single row may represent many
            // PatientData files (CLI-uploaded series come in as one entry with
            // grouped_item_ids). Expand them so the backend receives every file.
            if (Array.isArray(item.grouped_item_ids) && item.grouped_item_ids.length > 0) {
                dicom.patientDataIds.push(...item.grouped_item_ids);
            } else if (item.source_model === "PatientData") {
                dicom.patientDataIds.push(item.id);
            }
            continue;
        }
        const fp = (item.image || item.file || item.location || "").toLowerCase();
        const isRegularImage = regularImageExt.some((ext) => fp.endsWith(ext));
        const isImageDataType = item.data_type === "image" ||
            (item.data_type === "imaging" && item.original_file_type !== "dicom");
        if (isRegularImage || isImageDataType) regularImages.push(item);
    }
    return { dicom, dicomItems, regularImages, documents, signals, ehr, pathology };
};

// Returns the iframe URL for the given viewer at the given patient. Most viewer
// types are pure URL builders. OHIF variants and PME go through the backend
// /viewers/launch_viewer/ endpoint to upload DICOM and get the viewer URL back.
const buildEmbeddedViewerUrl = async ({ viewerType, patientId, studyData, studyId }) => {
    const patientItems = (studyData || []).filter(
        (item) => extractOriginalPatientId(item.patient_id) === patientId
    );
    if (patientItems.length === 0) {
        return { error: `No data found for ${patientId}.` };
    }
    const split = splitPatientItems(patientItems);

    if (viewerType.startsWith("ohif")) {
        if (split.dicom.medicalImageIds.length === 0 && split.dicom.patientDataIds.length === 0) {
            return { error: "No DICOM imaging for this patient." };
        }
        try {
            const resp = await API.post("/viewers/launch_viewer/", {
                viewer_name: viewerType,
                data_ids: split.dicom.medicalImageIds,
                patient_data_ids: split.dicom.patientDataIds,
                study_id: studyId,
            });
            try { await ensureOrthancAuthCookie(); } catch (e) { /* viewer surfaces */ }
            return { url: resp.data?.viewer_url || null };
        } catch (e) {
            return { error: e?.response?.data?.error || "Failed to launch viewer." };
        }
    }

    if (viewerType === "dv2") {
        const params = new URLSearchParams();
        params.append("patient_id", patientId);
        params.append("study_id", studyId);
        if (split.dicomItems.length) {
            params.append("dicom_data", split.dicomItems.map((i) => `${i.id}:${i.source_model || "MedicalImage"}`).join(","));
        }
        if (split.regularImages.length) {
            params.append("image_data", split.regularImages.map((i) => `${i.id}:${i.source_model || "MedicalImage"}`).join(","));
        }
        if (split.documents.length) {
            params.append("document_data", split.documents.map((i) => `${i.id}:${i.source_model || "MedicalDocument"}`).join(","));
        }
        if (split.signals.length) {
            params.append("signal_data", split.signals.map((i) => `${i.id}:${i.source_model || "PatientData"}`).join(","));
        }
        return { url: `/DV2?${params.toString()}` };
    }

    if (viewerType === "image-viewer") {
        if (split.regularImages.length === 0) return { error: "No images (JPG/PNG) for this patient." };
        const imageData = split.regularImages.map((i) => `${i.id}:${i.source_model || "MedicalImage"}`).join(",");
        return { url: `/Image_Viewer?image_data=${imageData}&study_id=${studyId}` };
    }

    if (viewerType === "ehr-viewer") {
        if (split.ehr.length === 0) return { error: "No EHR data for this patient." };
        const params = new URLSearchParams();
        params.append("study_id", studyId);
        params.append("patient_ids", patientId);
        params.append("ehr_data", split.ehr.map((i) => `${i.id}:${i.source_model || "PatientData"}`).join(","));
        return { url: `/EHR_Viewer?${params.toString()}` };
    }

    if (viewerType === "signal-viewer") {
        if (split.signals.length === 0) return { error: "No signal data for this patient." };
        const params = new URLSearchParams();
        params.append("study_id", studyId);
        params.append("patient_ids", patientId);
        params.append("signal_data", split.signals.map((i) => `${i.id}:${i.source_model || "PatientData"}`).join(","));
        return { url: `/Signal_Viewer?${params.toString()}` };
    }

    if (viewerType === "discovery-viewer-test") {
        if (split.regularImages.length === 0 || split.documents.length === 0) {
            return { error: "This viewer needs both an image and a document." };
        }
        const params = new URLSearchParams();
        params.append("patient_id", patientId);
        params.append("study_id", studyId);
        params.append("image_data", split.regularImages.map((i) => `${i.id}:${i.source_model || "MedicalImage"}`).join(","));
        params.append("document_data", split.documents.map((i) => `${i.id}:${i.source_model || "MedicalDocument"}`).join(","));
        return { url: `/Discovery_Viewer_Test?${params.toString()}` };
    }

    if (viewerType === "pme") {
        // PME goes through launch_viewer; the backend returns the base PME URL.
        // For embedded use we'd need slide filtering (see StudyDashboard.launchPme),
        // but the simplest correct embed is to hit the backend and use whatever
        // viewer_url it returns.
        try {
            const resp = await API.post("/viewers/launch_viewer/", {
                viewer_name: "pme",
                data_ids: [],
                patient_data_ids: [],
                study_id: studyId,
            });
            return { url: resp.data?.viewer_url || null };
        } catch (e) {
            return { error: e?.response?.data?.error || "Failed to launch PME viewer." };
        }
    }

    return { error: `Embedded "${viewerType}" is not supported yet.` };
};

// Renders the patient's first document inline. Browsers refuse to render
// .md/.txt etc. when the server sends Content-Disposition: attachment, so we
// fetch the bytes ourselves and render based on extension:
//   .md / .markdown → ReactMarkdown
//   .txt / .log / .csv / .json → <pre>
//   .html / .htm → iframe with srcDoc
//   .pdf and anything else → blob object URL (forces inline render)
// Patients can have multiple documents (e.g. the case description + an
// AI-model-context blurb). We list them all and let the reviewer flip between
// them; without this the panel silently dropped every doc past the first.
const EmbeddedDocument = ({ studyId, patientId, studyData, expanded, onToggleExpand }) => {
    const [docs, setDocs] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [state, setState] = useState({ status: "loading", content: null, blobUrl: null, kind: null, filename: null, error: null });

    // Recompute the doc list whenever the patient / study changes.
    useEffect(() => {
        const patientItems = (studyData || []).filter(
            (item) => extractOriginalPatientId(item.patient_id) === patientId
        );
        const docList = patientItems.filter(
            (i) => i.data_type === "document" || i.source_model === "MedicalDocument"
        );
        setDocs(docList);
        setSelectedIdx(0);
    }, [patientId, studyData]);

    // Load the currently-selected doc. Refetches on tab switch so each doc's
    // content is fresh — these payloads are small enough that caching isn't
    // worth the complexity.
    useEffect(() => {
        let cancelled = false;
        let createdBlobUrl = null;

        if (docs.length === 0) {
            setState({ status: "error", error: `No documents for ${patientId}.` });
            return () => { cancelled = true; };
        }
        const doc = docs[selectedIdx];
        if (!doc) {
            setState({ status: "error", error: "Document not available." });
            return () => { cancelled = true; };
        }

        const load = async () => {
            setState({ status: "loading", content: null, blobUrl: null, kind: null, filename: null, error: null });
            const filename = (doc.filename || doc.file || doc.location || "").toString().split("/").pop() || "document";
            const ext = filename.toLowerCase().split(".").pop();
            const isPatientData = doc.source_model === "PatientData";
            const isText = ["txt", "md", "markdown", "csv", "tsv", "json", "log", "html", "htm"].includes(ext);

            try {
                if (isText) {
                    // Use the same study-scoped / data_manager endpoints CustomViewer
                    // uses so auth and study access control are consistent.
                    const contentUrl = isPatientData
                        ? (studyId
                            ? `/studies/${studyId}/patient-data/${doc.id}/content/`
                            : `/data_manager/patient-data/${doc.id}/content/`)
                        : `/data_manager/medical-documents/${doc.id}/content/`;
                    const resp = await API.get(contentUrl);
                    const content = resp?.data?.content || "";
                    if (cancelled) return;
                    if (ext === "md" || ext === "markdown") {
                        setState({ status: "ready", kind: "markdown", content, filename });
                    } else if (ext === "html" || ext === "htm") {
                        setState({ status: "ready", kind: "html", content, filename });
                    } else {
                        setState({ status: "ready", kind: "text", content, filename });
                    }
                } else {
                    // Binary (PDF, image, etc.): there's no /file/ endpoint, so iframe
                    // the FileField URL directly. The browser sends the orthanc_auth
                    // cookie that nginx already accepts for /media/ paths in this app.
                    // doc.file is typically a relative URL like /media/private/...
                    const rawUrl = doc.file || doc.location;
                    if (!rawUrl) throw new Error("Document has no file URL.");
                    if (!cancelled) setState({ status: "ready", kind: "iframe", blobUrl: rawUrl, filename });
                }
            } catch (err) {
                const status = err?.response?.status ? `HTTP ${err.response.status}` : (err.message || "Failed to load document.");
                if (!cancelled) setState({ status: "error", error: status });
            }
        };

        load();
        return () => {
            cancelled = true;
            if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
        };
    }, [studyId, docs, selectedIdx, patientId]);

    const tabLabel = (doc) => {
        const raw = (doc.filename || doc.file || doc.location || "").toString().split("/").pop() || "document";
        // Strip extension for the tab label to keep things tight.
        return raw.replace(/\.[^.]+$/, "");
    };

    return (
        <div className={`slr-card slr-viewer ${expanded ? "expanded" : ""}`} data-slr-document="1">
            <div className="slr-viewer-toolbar">
                <span><i className="fa fa-file-text me-2"></i>document viewer</span>
                {docs.length > 1 && (
                    <div className="slr-doc-tabs" role="tablist" style={{ display: "flex", gap: 4, marginLeft: 8, flexWrap: "wrap" }}>
                        {docs.map((d, i) => (
                            <button
                                key={d.id ?? i}
                                type="button"
                                role="tab"
                                aria-selected={selectedIdx === i}
                                className={`slr-doc-tab ${selectedIdx === i ? "active" : ""}`}
                                onClick={() => setSelectedIdx(i)}
                                title={d.filename || tabLabel(d)}
                                style={{
                                    padding: "2px 10px",
                                    borderRadius: 4,
                                    border: "1px solid rgba(125, 211, 252, 0.4)",
                                    background: selectedIdx === i ? "rgba(125, 211, 252, 0.25)" : "transparent",
                                    color: "inherit",
                                    fontSize: 12,
                                    cursor: "pointer",
                                }}
                            >
                                {tabLabel(d)}
                            </button>
                        ))}
                    </div>
                )}
                <button type="button" className="slr-icon-btn" onClick={onToggleExpand} title={expanded ? "Collapse" : "Expand"} style={{ marginLeft: "auto" }}>
                    <i className={`fa ${expanded ? "fa-compress" : "fa-expand"}`}></i>
                </button>
            </div>
            <div className="slr-viewer-stage slr-document-stage">
                {state.status === "loading" && (
                    <div className="slr-viewer-placeholder">
                        <i className="fa fa-spinner fa-spin"></i><p>Loading document…</p>
                    </div>
                )}
                {state.status === "error" && (
                    <div className="slr-viewer-placeholder">
                        <i className="fa fa-exclamation-triangle"></i><p>{state.error}</p>
                    </div>
                )}
                {state.status === "ready" && state.kind === "markdown" && (
                    <div className="slr-document-content slr-markdown">
                        <ReactMarkdown>{state.content}</ReactMarkdown>
                    </div>
                )}
                {state.status === "ready" && state.kind === "text" && (
                    <pre className="slr-document-content slr-pre">{state.content}</pre>
                )}
                {/* srcDoc below renders API-supplied HTML from an uploaded document.
                    Unsandboxed it inherits the SPA origin, making any uploaded .html
                    stored XSS with access to session state. allow-same-origin is
                    deliberately absent from the sandbox tokens -- that is what severs it. */}
                {state.status === "ready" && state.kind === "html" && (
                    <iframe
                      title={state.filename}
                      srcDoc={state.content}
                      className="slr-viewer-iframe"
                      sandbox=""
                      referrerPolicy="no-referrer"
                    />
                )}
                {state.status === "ready" && state.kind === "iframe" && (
                    <iframe
                      title={state.filename}
                      src={resolveAllowedViewerUrl(state.blobUrl)}
                      className="slr-viewer-iframe"
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                      referrerPolicy="no-referrer"
                    />
                )}
            </div>
        </div>
    );
};

const EmbeddedViewer = ({ comp, studyId, patientId, studyData, expanded, onToggleExpand }) => {
    const viewerType = comp.props?.viewer_type || "ohif";

    const [iframeUrl, setIframeUrl] = useState(null);
    const [launchError, setLaunchError] = useState(null);
    const [launching, setLaunching] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const launch = async () => {
            setLaunching(true);
            setLaunchError(null);
            setIframeUrl(null);
            const { url, error } = await buildEmbeddedViewerUrl({ viewerType, patientId, studyData, studyId });
            if (cancelled) return;
            if (error) setLaunchError(error);
            else setIframeUrl(url);
            setLaunching(false);
        };
        launch();
        return () => { cancelled = true; };
    }, [viewerType, patientId, studyId, studyData]);

    return (
        <div className={`slr-card slr-viewer ${expanded ? "expanded" : ""}`}>
            <div className="slr-viewer-toolbar">
                <span><i className="fa fa-eye me-2"></i>{viewerType}</span>
                <button
                    type="button"
                    className="slr-icon-btn"
                    onClick={onToggleExpand}
                    title={expanded ? "Collapse" : "Expand"}
                >
                    <i className={`fa ${expanded ? "fa-compress" : "fa-expand"}`}></i>
                </button>
            </div>
            <div className="slr-viewer-stage">
                {launching ? (
                    <div className="slr-viewer-placeholder">
                        <i className="fa fa-spinner fa-spin"></i>
                        <p>Loading viewer…</p>
                    </div>
                ) : launchError ? (
                    <div className="slr-viewer-placeholder">
                        <i className="fa fa-exclamation-triangle"></i>
                        <p>{launchError}</p>
                    </div>
                ) : iframeUrl ? (
                    <iframe
                        title={`${viewerType}-${patientId}`}
                        src={resolveAllowedViewerUrl(iframeUrl)}
                        className="slr-viewer-iframe"
                        referrerPolicy="no-referrer"
                        // Used by the AI panel's "Capture viewer" button to skip
                        // non-imaging iframes (the document viewer's iframe also
                        // carries the slr-viewer-iframe class).
                        data-slr-imaging="1"
                        allow="fullscreen"
                    />
                ) : null}
            </div>
        </div>
    );
};

export default ScrollableLayoutRenderer;
